import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SapCreateRequest,
  SapUpdateRequest,
  SapListQuery,
  SapListResponse,
  SapResponse,
} from '@produzionejb/types';
import { api } from './api-client';

const QK = {
  list: (q: Partial<SapListQuery>) => ['sap-documents', 'list', q] as const,
  one: (id: string) => ['sap-documents', 'one', id] as const,
};

function toQueryString(q: Partial<SapListQuery>): string {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.status) params.set('status', q.status);
  if (q.periodId) params.set('periodId', q.periodId);
  if (q.search) params.set('search', q.search);
  if (q.workDateFrom) params.set('workDateFrom', new Date(q.workDateFrom).toISOString());
  if (q.workDateTo) params.set('workDateTo', new Date(q.workDateTo).toISOString());
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useSapList(query: Partial<SapListQuery>) {
  return useQuery({
    queryKey: QK.list(query),
    queryFn: () => api<SapListResponse>(`/api/documents/sap${toQueryString(query)}`),
    staleTime: 10_000,
  });
}

export function useSap(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ['sap-documents', 'one', 'NIL'],
    queryFn: () => api<SapResponse>(`/api/documents/sap/${id}`),
    enabled: !!id,
  });
}

export function useCreateSap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SapCreateRequest) =>
      api<SapResponse>('/api/documents/sap', { method: 'POST', body: data }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sap-documents', 'list'] });
    },
  });
}

export function useUpdateSap(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SapUpdateRequest) =>
      api<SapResponse>(`/api/documents/sap/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['sap-documents', 'list'] });
    },
  });
}

export function useCloseSap(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<SapResponse>(`/api/documents/sap/${id}/close`, { method: 'POST', body: {} }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['sap-documents', 'list'] });
    },
  });
}

export function useDeleteSap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/documents/sap/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sap-documents', 'list'] });
    },
  });
}
